/**
 * Billing — live data pulled from the payment providers, so support doesn't
 * have to open FastSpring/Stripe in another tab to answer "what did they pay".
 *
 * Read-only for now. A provider we couldn't reach is shown as such, never as
 * "no subscriptions" — the difference matters when deciding to issue a refund.
 */
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import {
  BILLING_PROVIDER_LABELS,
  type BillingSnapshot,
  type UserAccount,
} from "../../../../../shared/index";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Card, EmptyState } from "./parts";
import { ProviderBadge } from "./provider-badge";
import { describeError, formatDate, formatMoney, relativeDate } from "./format";
import { Copyable } from "./copyable";
import { EmailTag } from "./email-tag";
import {
  providerCustomerUrl,
  providerSubscriptionUrl,
} from "./provider-links";

export function BillingTab({ account }: { account: UserAccount }) {
  const email = account.email;
  const billing = useQuery({
    queryKey: ["site-user-billing", email],
    queryFn: () => api.siteUsers.billing(email),
    retry: false,
  });

  // Provider accounts are keyed by email, and an address freed up by an email
  // change can be re-registered by someone else. Payments that predate this
  // account may therefore belong to the address's previous owner.
  const registered = account.registrationDate;
  const predatesAccount = (date: string | null) =>
    Boolean(registered && date && date < registered.slice(0, 10));

  if (billing.isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Asking the payment providers…
      </div>
    );
  }

  if (billing.isError) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
        <CircleAlert className="mt-0.5 h-4 w-4 text-destructive" />
        <p>{describeError(billing.error)}</p>
      </div>
    );
  }

  const queried = billing.data?.emailsQueried ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {queried.length > 1 ? (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>Searched under {queried.length} addresses:</span>
            {queried.map((e, i) => (
              <EmailTag key={e} email={e} all={queried} current={i === 0} />
            ))}
          </div>
        ) : (
          <span />
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => billing.refetch()}
          disabled={billing.isFetching}
        >
          <RefreshCw
            className={cn("mr-2 h-4 w-4", billing.isFetching && "animate-spin")}
          />
          Refresh
        </Button>
      </div>
      {(billing.data?.snapshots ?? []).some((s) =>
        s.events.some((e) => predatesAccount(e.date))
      ) && (
        <div className="flex items-start gap-3 rounded-xl border border-warning/50 bg-warning/10 p-4 text-sm">
          <CircleAlert className="mt-0.5 h-4 w-4" />
          <p>
            Some payments predate this account's registration (
            {formatDate(registered)}). Provider accounts are keyed by email, so
            these may belong to a previous owner of the address. Marked{" "}
            <Badge variant="warning">pre-dates account</Badge> below.
          </p>
        </div>
      )}

      {(billing.data?.snapshots ?? []).map((snapshot) => (
        <ProviderCard
          key={snapshot.provider}
          snapshot={snapshot}
          allEmails={queried}
          predatesAccount={predatesAccount}
        />
      ))}
    </div>
  );
}

function ProviderCard({
  snapshot,
  allEmails,
  predatesAccount,
}: {
  snapshot: BillingSnapshot;
  /** Every address queried, current first — drives the tag colours. */
  allEmails: string[];
  predatesAccount: (date: string | null) => boolean;
}) {
  const label = BILLING_PROVIDER_LABELS[snapshot.provider];
  const multipleEmails = allEmails.length > 1;

  if (!snapshot.available) {
    return (
      <Card
        title={label}
        actions={<Badge variant="secondary">Unavailable</Badge>}
        titleBadge={<ProviderBadge provider={snapshot.provider} showLabel={false} />}
      >
        <p className="text-sm text-muted-foreground">
          {snapshot.unavailableReason}
        </p>
      </Card>
    );
  }

  if (snapshot.customers.length === 0) {
    return (
      <Card
        title={label}
        titleBadge={<ProviderBadge provider={snapshot.provider} showLabel={false} />}
        actions={<Badge variant="outline">No customer</Badge>}
      >
        <EmptyState>
          No {label} customer under{" "}
          {multipleEmails ? "any of these addresses" : "this address"}.
        </EmptyState>
      </Card>
    );
  }

  return (
    <Card
      title={label}
      description={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {snapshot.customers.map((c) => {
            const url = providerCustomerUrl(snapshot.provider, c.customerId);
            return (
              <span key={c.customerId} className="flex items-center gap-1">
                <Copyable value={c.customerId} mono />
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    title="Open in the provider dashboard"
                    className="text-primary hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {multipleEmails && (
                  <EmailTag email={c.email} all={allEmails} />
                )}
              </span>
            );
          })}
        </span>
      }
      titleBadge={<ProviderBadge provider={snapshot.provider} showLabel={false} />}
      actions={<Badge variant="success">Live</Badge>}
    >
      <div className="space-y-6">
        <div>
          <h4 className="mb-2 text-sm font-medium">Subscriptions</h4>
          {snapshot.subscriptions.length === 0 ? (
            <EmptyState>No subscriptions at {label}.</EmptyState>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Interval</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Next charge</TableHead>
                  {multipleEmails && <TableHead>Found under</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {snapshot.subscriptions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        {sub.product ?? sub.id}
                        {sub.autoRenew === false && (
                          <Badge variant="warning">no auto-renew</Badge>
                        )}
                      </span>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Copyable value={sub.id} mono />
                        {providerSubscriptionUrl(snapshot.provider, sub.id) && (
                          <a
                            href={providerSubscriptionUrl(snapshot.provider, sub.id)!}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          sub.status === "active" ? "success" : "secondary"
                        }
                      >
                        {sub.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatMoney(sub.amount, sub.currency)}
                      {sub.listAmount != null &&
                        sub.amount != null &&
                        sub.listAmount > sub.amount && (
                          <span
                            className="block text-xs text-muted-foreground line-through"
                            title="List price before discount"
                          >
                            {formatMoney(sub.listAmount, sub.currency)}
                          </span>
                        )}
                    </TableCell>
                    <TableCell>{sub.interval ?? "—"}</TableCell>
                    <TableCell>{formatDate(sub.startDate)}</TableCell>
                    <TableCell>
                      {sub.cancelledDate ? (
                        `cancelled ${formatDate(sub.cancelledDate)}`
                      ) : (
                        <>
                          {formatDate(sub.nextChargeDate)}
                          {relativeDate(sub.nextChargeDate) && (
                            <span className="block text-xs text-muted-foreground">
                              {relativeDate(sub.nextChargeDate)}
                            </span>
                          )}
                        </>
                      )}
                    </TableCell>
                    {multipleEmails && (
                      <TableCell>
                        <EmailTag
                          email={sub.sourceEmail}
                          all={allEmails}
                          current={
                            sub.sourceEmail.toLowerCase() ===
                            allEmails[0]?.toLowerCase()
                          }
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div>
          <h4 className="mb-2 text-sm font-medium">Payments & refunds</h4>
          {snapshot.events.length === 0 ? (
            <EmptyState>No payment history at {label}.</EmptyState>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Paid with</TableHead>
                  <TableHead>For</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {snapshot.events.map((event) => (
                  <TableRow key={`${event.type}:${event.id}`}>
                    <TableCell>
                      {formatDate(event.date, true)}
                      {predatesAccount(event.date) && (
                        <Badge variant="warning" className="ml-2">
                          pre-dates account
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          event.type === "refund" ? "warning" : "secondary"
                        }
                      >
                        {event.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatMoney(event.amount, event.currency)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {event.paymentMethod ?? "—"}
                    </TableCell>
                    <TableCell className="max-w-[14rem] truncate text-xs">
                      {event.items.join(", ") || "—"}
                    </TableCell>
                    <TableCell>{event.status ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {event.invoiceUrl ? (
                        <a
                          href={event.invoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          {event.reference ?? event.id}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        (event.reference ?? event.id)
                      )}
                      {multipleEmails && (
                        <EmailTag
                          email={event.sourceEmail}
                          all={allEmails}
                          current={
                            event.sourceEmail.toLowerCase() ===
                            allEmails[0]?.toLowerCase()
                          }
                          className="mt-1 font-sans"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </Card>
  );
}
